'use client';
import React, { memo, Suspense, useMemo } from 'react';
import { invariant } from 'ts-invariant';
import type { ComputedBlockStateUnionType } from '@unifyapps/defs/types/block';
import { compose } from '../../../utils/compose';
import { useSetBlockRef } from '../../hooks/useSetBlockRef';
import useDerivedEvents from '../../hooks/useDerivedEvents';
import type { BlockRendererProps, BlockRendererWrapperProps } from '../RegistryProvider/types';
import { useRegistryContext } from '../RegistryProvider';
import { useBlockDataAttributes } from '../../hooks/useBlockDataAttributes';
import { useInterfaceStore } from '../../stores/InterfaceStore';
import { useDependencyGraphProvider } from '../../context/DependencyGraphContext';
import { DependencyHelper } from '../../../dependency/helpers/DependencyHelper';
import { BlockHelper } from '../../../helpers/BlockHelper';
import { useComputeBlockStateFromPaths } from '../../hooks/useComputeBlockState';
import { withVisibilityPermissionsAndDetachedCheck } from './hocs/withVisibilityAndPermissionsCheck';
import { withContext } from './hocs/withContext';
import { withBlockState } from './hocs/withBlockState';
import { withBlockRuntimeState } from './hocs/withBlockRuntimeState';
import { withBlockPage } from './hocs/withBlockPage';
import { withBlockEffects } from './hocs/withBlockEffects';

export function BlockRenderer({
  block,
  blockState,
  updateBlockState,
  context,
  updateBlockSessionState,
  getGlobalStoreContext,
  dndParams,
  dataAttributes: propsDataAttributes,
}: BlockRendererProps) {
  const { id, component, dataSourceIds, events, fallback } = block;
  const { registry } = useRegistryContext();
  const { buildEntityDependency } = useDependencyGraphProvider();
  const Block = registry.getBlockComponent(component.componentType);
  const activeInterfacePageId = useInterfaceStore().use.activeInterfacePageId();
  const { blockRef } = useSetBlockRef({
    blockState,
  });
  const { shouldUseBuiltDependencies } = useInterfaceStore().use.page.flags() ?? {};

  invariant(blockState, `Block state is not defined for block with ${blockState?.id ?? block.id}`);

  const { dynPaths, condPaths } = useMemo(() => {
    if (shouldUseBuiltDependencies) {
      return {
        dynPaths: BlockHelper.getDynamicPaths(block),
        condPaths: BlockHelper.getConditionalPaths(block),
      };
    }
    const blockDeps = buildEntityDependency(blockState, 'blocks')[blockState.id];
    return {
      dynPaths: DependencyHelper.getDynamicPaths(blockDeps),
      condPaths: DependencyHelper.getConditionalPaths(blockDeps),
    };
  }, [block, blockState, buildEntityDependency, shouldUseBuiltDependencies]);

  const computedBlockState = useComputeBlockStateFromPaths<ComputedBlockStateUnionType>({
    blockState,
    context,
    dynPaths,
    condPaths,
  });

  const { derivedEvents } = useDerivedEvents({
    blockStateId: blockState.id,
  });

  const dataAttributes = useBlockDataAttributes({
    id,
    component,
    pageId: activeInterfacePageId,
    //blockStateId will be different from blockId in case of runtime blocks
    blockStateId: blockState.id,
  });

  const attributesAndEventListeners = useMemo(
    () =>
      ({
        ...dataAttributes,
        ...propsDataAttributes,
        ...dndParams?.attributes,
        ...dndParams?.listeners,
      }) as Record<string, unknown>,
    [dataAttributes, dndParams?.attributes, dndParams?.listeners, propsDataAttributes],
  );

  // The platform automatically configures derived block events for a block.
  // Place derived events first to ensure they are fired first,
  // as events before them might take too much time and prevent them from firing.
  const allEvents = useMemo(() => {
    return [...derivedEvents, ...(events || [])];
  }, [events, derivedEvents]);

  if (!Block) {
    console.error(
      'Component type not recognized: ',
      component,
      ' in BlocksRegistry ',
      registry.getRegistryName(),
    );
    return null; // or some fallback UI
  }

  // Suspense boundaries were removed to fix 100% CPU utilization issue for few seconds after page load in preference management app
  // It was only happening for SSR pages
  // (https://qa.unifyapps.com/p/0/interfaces/preference-mngmnt/overview?e_interface_q=pref)
  // The exact reason why removing suspense boundaries fixes this issue is still unclear
  return (
    <Block
      attributesAndEventListeners={attributesAndEventListeners}
      block={block}
      blockRef={blockRef}
      blockState={blockState}
      component={component}
      computedBlockState={computedBlockState}
      dataSourceIds={dataSourceIds}
      dndRef={dndParams?.ref}
      events={allEvents}
      fallback={fallback}
      getGlobalStoreContext={getGlobalStoreContext}
      key={id}
      registry={registry}
      updateBlockSessionState={updateBlockSessionState}
      updateBlockState={updateBlockState}
    />
  );
}

/** IMPORTANT:
 * the order of the runner hocs is important, it helps in preventing unnecessary re-renders
 * at first, we get all the block state, either from the global state store or if the runtime block state is changed, we get it from the runtime block state
 * now, when the block state is changed, we have to re-render, in this situation, withContext, prepares the new context,
 * and BlockRenderer uses it to compute the dynamicPaths of the block
 * In case of notifyChange from other entities, useSyncExternalStore of withContext is used to re-render the block, whenever it triggers a re-render, we know that new context is built.
 * In the end we have all the context and block state ready to compute the visibility and permissions of the block
 */
export const runnerBlockRendererComposer = compose<React.ComponentType<BlockRendererWrapperProps>>(
  memo,
  withBlockPage,
  withBlockRuntimeState,
  withBlockState,
  withContext,
  withBlockEffects,
  withVisibilityPermissionsAndDetachedCheck,
);

export const RunnerBlockRenderer = runnerBlockRendererComposer(BlockRenderer);

export default RunnerBlockRenderer;
